/**
 * PaymentMethodsSection - Manage saved payment methods for bidding
 * Uses Stripe SetupIntent for off-session card saving
 */
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";
import {
  CreditCard, Plus, Trash2, Check, Loader2, Shield, AlertTriangle, Star
} from "lucide-react";

export default function PaymentMethodsSection() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [settingDefault, setSettingDefault] = useState(null);
  
  // Card form state
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvc, setCvc] = useState("");

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/billing/payment-methods");
      setPaymentMethods(response.data?.payment_methods || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleAddCard = async (e) => {
    e.preventDefault();
    
    if (!cardNumber || !expMonth || !expYear || !cvc) {
      toast.error("Please fill in all card details");
      return;
    }
    
    setAdding(true);
    try {
      // First, create a SetupIntent
      const setupResponse = await api.post("/billing/setup-intent");
      const { client_secret, customer_id } = setupResponse.data;
      
      if (!client_secret) {
        throw new Error("Failed to create setup intent");
      }
      
      // Then confirm with card details
      const confirmResponse = await api.post("/billing/confirm-setup", {
        client_secret,
        card_number: cardNumber.replace(/\s/g, ""),
        exp_month: parseInt(expMonth),
        exp_year: parseInt(expYear.length === 2 ? "20" + expYear : expYear),
        cvc
      });
      
      toast.success("Card added successfully!");
      setShowAddForm(false);
      setCardNumber("");
      setExpMonth("");
      setExpYear("");
      setCvc("");
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add card");
    }
    setAdding(false);
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!confirm("Are you sure you want to remove this payment method?")) return;
    
    setDeleting(paymentMethodId);
    try {
      await api.delete(`/billing/payment-methods/${paymentMethodId}`);
      toast.success("Card removed successfully");
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove card");
    }
    setDeleting(null);
  };

  const handleSetDefault = async (paymentMethodId) => {
    setSettingDefault(paymentMethodId);
    try {
      await api.post(`/billing/payment-methods/${paymentMethodId}/default`);
      toast.success("Default card updated");
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update default card");
    }
    setSettingDefault(null);
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 16);
    const parts = cleaned.match(/.{1,4}/g) || [];
    return parts.join(" ");
  };

  const getCardBrandIcon = (brand) => {
    // Return brand-specific icon or default credit card
    return CreditCard;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Payment Methods</h3>
          <p className="text-sm text-muted-foreground">
            Manage your saved cards for bidding and purchases
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} data-testid="add-card-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </Button>
        )}
      </div>

      {/* Bidding Notice */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-500">Required for Bidding</p>
            <p className="text-sm text-muted-foreground">
              A saved payment method is required to place bids. If you win an auction, 
              your default card will be charged automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Add Card Form */}
      {showAddForm && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="text-lg">Add New Card</CardTitle>
            <CardDescription>Enter your card details below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddCard} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cardNumber">Card Number</Label>
                <Input
                  id="cardNumber"
                  type="text"
                  placeholder="4242 4242 4242 4242"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  className="font-mono"
                  maxLength={19}
                  data-testid="card-number-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expMonth">Month</Label>
                  <Input
                    id="expMonth"
                    type="text"
                    placeholder="MM"
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, "").slice(0, 2))}
                    maxLength={2}
                    data-testid="exp-month-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expYear">Year</Label>
                  <Input
                    id="expYear"
                    type="text"
                    placeholder="YY"
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    maxLength={4}
                    data-testid="exp-year-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cvc">CVC</Label>
                  <Input
                    id="cvc"
                    type="text"
                    placeholder="123"
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    maxLength={4}
                    data-testid="cvc-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={adding} data-testid="save-card-btn">
                  {adding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Card
                    </>
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Saved Cards */}
      {paymentMethods.length === 0 && !showAddForm ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-1">No payment methods saved</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add a card to start bidding on auctions
            </p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Card
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map((pm) => {
            const BrandIcon = getCardBrandIcon(pm.brand);
            return (
              <Card key={pm.id} className={pm.is_default ? "border-primary" : ""}>
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-8 bg-slate-800 rounded flex items-center justify-center">
                      <BrandIcon className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {pm.brand?.toUpperCase() || "Card"} •••• {pm.last4}
                        </span>
                        {pm.is_default && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expires {pm.exp_month}/{pm.exp_year}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!pm.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetDefault(pm.id)}
                        disabled={settingDefault === pm.id}
                      >
                        {settingDefault === pm.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Star className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteCard(pm.id)}
                      disabled={deleting === pm.id}
                    >
                      {deleting === pm.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-4 h-4" />
        <span>Your card information is encrypted and stored securely with Stripe</span>
      </div>
    </div>
  );
}
